import { writable, type Writable } from 'svelte/store';
import { browser } from '$app/environment';

type SerializableValue = string | number | boolean | object | null;

export interface LocalStorageStore<T> extends Writable<T> {
	remove(): void;
	exists(): boolean;
}

/**
 * Creates a writable store that syncs with localStorage
 * @param key The localStorage key to sync with
 * @param initialValue The initial value if no value exists in localStorage
 */
function safeLocalStorage(): Storage | null {
	if (!browser) return null;
	try {
		const ls = window.localStorage;
		if (!ls || typeof ls.getItem !== 'function') return null;
		return ls;
	} catch {
		// Some browser configurations (private mode, disabled storage) throw on
		// access. Treat as unavailable.
		return null;
	}
}

export function createLocalStorage<T extends SerializableValue>(
	key: string,
	initialValue: T
): LocalStorageStore<T> {
	let initial = initialValue;
	const ls = safeLocalStorage();

	if (ls) {
		try {
			const storedValue = ls.getItem(key);
			if (storedValue) {
				initial = JSON.parse(storedValue);
			}
		} catch (e) {
			console.error(`Failed to read/parse localStorage key "${key}":`, e);
		}
	}

	const store = writable<T>(initial);

	if (ls) {
		store.subscribe((value) => {
			try {
				if (value === null || value === undefined) {
					ls.removeItem(key);
				} else {
					ls.setItem(key, JSON.stringify(value));
				}
			} catch {
				// Quota exceeded or storage disabled mid-session — drop silently.
			}
		});
	}

	return {
		...store,
		remove: () => {
			store.set(initialValue);
			if (ls) {
				try {
					ls.removeItem(key);
				} catch {
					// no-op
				}
			}
		},
		exists: () => {
			if (!ls) return false;
			try {
				return ls.getItem(key) !== null;
			} catch {
				return false;
			}
		}
	};
}

/**
 * Simple utility functions for direct localStorage access
 */
export const storage = {
	get: <T = SerializableValue>(key: string): T | null => {
		if (!browser) return null;
		const item = window.localStorage.getItem(key);
		try {
			return item ? JSON.parse(item) : null;
		} catch {
			return null;
		}
	},

	set: (key: string, value: SerializableValue): void => {
		if (!browser) return;
		window.localStorage.setItem(key, JSON.stringify(value));
	},

	remove: (key: string): void => {
		if (!browser) return;
		window.localStorage.removeItem(key);
	},

	clear: (): void => {
		if (!browser) return;
		window.localStorage.clear();
	}
};
